import { Injectable } from '@angular/core';
import { FiBackendService } from '@fi-shared/fi-backend/fi-backend.service';
import {
    IFiChecklistAnswerModel,
    IFiChecklistViewerModel,
    IFiCheckPointAnswerHistoryViewModel,
} from '@fi-shared/checklist';
import { IMediaBankFileReference } from '@fi-shared/media-bank';
import { FiCheckPointAnswerHistoryType } from '@fi-shared/checklist/checklist-types';
import { CheckpointStatusType } from '@fi-shared/checklist';
import { AppTextService } from '@fi-shared/app-text';
import { ObjectTypes } from '@fi-shared/types';
import { comparator } from '@fi-shared/utils/array-sort-comparators';
import { ICommentModel } from '@fi-shared/fi-comments/fi-comments';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { Subject } from 'rxjs';
import { FiSignalRService } from '@fi-shared/fi-signal-r/fi-signal-r.service';
import { ICheckPointNotApplicableItem } from '@fi-shared/checklist/checklist';
import { IChecklistV2 } from '@fi-shared/checklist/checklist-v2';
import { IItemsListDataFetchResult, IItemsListParameterBase } from '@fi-shared/fi-items-list/fi-items-list';

@Injectable()
export class FiChecklistService {
    constructor(
        protected fiBackend: FiBackendService,
        protected appTextService: AppTextService,
        protected fiLoadingService: LoadingService,
        protected fiSignalRService: FiSignalRService,
    ) {}

    setChecklistNotApplicable(id, isSetNoApplicable) {
        return this.fiBackend.fetchDataPost<boolean>(`CheckList/CheckList/SetNotApplicable/${id}/${isSetNoApplicable}`);
    }

    saveChecklistViewer(checklistViewer, reqConfig?) {
        return this.fiBackend.fetchDataPost<void>(
            'CheckList/CheckList/SaveCheckListViewer',
            checklistViewer,
            reqConfig,
        );
    }

    setCheckPointsNotApplicable(notApplicableData: ICheckPointNotApplicableItem[]) {
        return this.fiBackend.fetchDataPost<void>('CheckList/CheckList/SetCheckPointsNotApplicable', notApplicableData);
    }

    getCheckList(id, reqProjFn) {
        return this.fiBackend.fetchObjectProj('CheckList/CheckList/GetCheckList/' + id, reqProjFn);
    }

    saveCheckList(checklist) {
        return this.fiBackend.fetchDataPost<number>('CheckList/CheckList/SaveCheckList', checklist);
    }

    public getCheckListViewer(checklistId: number, reqConfig?): Promise<IFiChecklistViewerModel> {
        return this.fiBackend.fetchData<IFiChecklistViewerModel>(
            `CheckList/CheckList/GetCheckListViewer/${checklistId}`,
            reqConfig,
        );
    }

    getChecklistHistories(
        answer: IFiChecklistAnswerModel,
        mediaBankReferences: IMediaBankFileReference[],
        comment: ICommentModel,
    ) {
        const result: IFiCheckPointAnswerHistoryViewModel[] = [];
        for (let idx = 0; idx < answer.Histories.length; idx++) {
            const history = answer.Histories[idx];
            const prevAnswer: CheckpointStatusType = idx > 0 ? answer.Histories[idx - 1].Answer : null;
            let currentAnswer = history.Answer;
            let historyType = FiCheckPointAnswerHistoryType.Unknown;
            if (!prevAnswer) {
                if (currentAnswer === CheckpointStatusType.Ok) historyType = FiCheckPointAnswerHistoryType.ToOk;
                else if (currentAnswer === CheckpointStatusType.NotOk)
                    historyType = FiCheckPointAnswerHistoryType.ToNotOk;
            } else {
                if (prevAnswer === CheckpointStatusType.Ok) {
                    if (currentAnswer === CheckpointStatusType.NotOk)
                        historyType = FiCheckPointAnswerHistoryType.FromOkToNotOk;
                    else if (currentAnswer === null) historyType = FiCheckPointAnswerHistoryType.FromOkToNull;
                } else if (prevAnswer === CheckpointStatusType.NotOk) {
                    if (currentAnswer === CheckpointStatusType.Ok)
                        historyType = FiCheckPointAnswerHistoryType.FromNotOkToOk;
                    else if (currentAnswer === null) historyType = FiCheckPointAnswerHistoryType.FromNotOkToNull;
                }
            }

            let text = '';
            if (historyType === FiCheckPointAnswerHistoryType.ToOk)
                text = this.appTextService.get('checkpoint.answer_to_ok');
            else if (historyType === FiCheckPointAnswerHistoryType.ToNotOk)
                text = this.appTextService.get('checkpoint.answer_to_not_ok');
            else if (historyType === FiCheckPointAnswerHistoryType.FromNotOkToOk)
                text = this.appTextService.get('checkpoint.answer_from_not_ok_to_ok');
            else if (historyType === FiCheckPointAnswerHistoryType.FromOkToNotOk)
                text = this.appTextService.get('checkpoint.answer_from_ok_to_not_ok');
            else if (
                historyType === FiCheckPointAnswerHistoryType.FromNotOkToNull ||
                historyType === FiCheckPointAnswerHistoryType.FromOkToNull
            )
                text = this.appTextService.get('checkpoint.answer_cleared');

            if (text) {
                result.push({
                    createdByUserId: history.CreatedByUserId,
                    createdByUserName: history.CreatedByUserName,
                    createdOn: history.CreatedOn,
                    createdByProfile: history.CreatedByProfile,
                    content: text,
                    objectType: ObjectTypes.CheckPointAnswer,
                });
            }
        }
        if (comment) {
            result.push({
                createdByUserId: comment.CreatedByUserId,
                createdByUserName: comment.CreatedByUserName,
                createdOn: comment.CreatedOn,
                createdByProfile: comment.UserProfile,
                content: comment.Content,
                objectType: ObjectTypes.Comment,
            });
        }
        // todo: replace this condition in right place after Mattias answers the questions in https://chainformation.atlassian.net/browse/FI-4011
        if (mediaBankReferences.length) {
            result.push({
                createdByUserId: mediaBankReferences[0].CreatedByUserId,
                createdByUserName: mediaBankReferences[0].CreatedByUserName,
                createdOn: mediaBankReferences[0].CreatedOn,
                createdByProfile: {
                    Id: mediaBankReferences[0].CreatedByUserId,
                    FullName: mediaBankReferences[0].CreatedByUserName,
                    MediaBankPhysicalFileName: mediaBankReferences[0].CreatedByMediaBankPhysicalFileName,
                    MediaBankExtension: mediaBankReferences[0].Extension,
                },
                mediaBankReferences: mediaBankReferences,
                objectType: ObjectTypes.MediaBank,
            });
        }
        result.sort(comparator((item) => new Date(item.createdOn).getTime()));
        return result;
    }

    turnOffChecklistReminder(id: number, isTurnOffReminder: boolean) {
        return this.fiLoadingService.trackPromise(
            this.fiBackend.fetchDataPost<boolean>(`CheckList/CheckList/TurnOffReminder/${id}/${isTurnOffReminder}`),
        );
    }

    markChecklistAsVisited(id: number) {
        return this.fiBackend.fetchDataPost('CheckList/CheckList/MarkCheckListAsVisited/' + id);
    }

    subscribeOnChecklistAnswersChange(args: {
        checkListId: number;
        periodId: number;
        businessUnitId: number;
        answers: IFiChecklistAnswerModel[];
        ngOnDestroy$: Subject<void>;
        afterChange: () => void;
    }) {
        this.fiSignalRService.checkListViewerChangedEvent.subscribe((data) => {
            if (
                data.CheckListId === args.checkListId &&
                data.CheckListPeriodId === args.periodId &&
                data.BusinessUnitId === args.businessUnitId
            ) {
                args.answers.forEach((answer) => {
                    const updatedAnswer = data.CheckPointAnswers.find(
                        (updatedAnswer) => updatedAnswer.CheckPointId === answer.CheckPointId,
                    );
                    if (updatedAnswer) {
                        answer.Answer = updatedAnswer.Answer;
                        answer.ChangedByUserId = updatedAnswer.ChangedByUserId;
                        answer.ChangedByUserName = updatedAnswer.ChangedByUserName;
                        answer.ChangedOn = updatedAnswer.ChangedOn;
                        answer.MediaBankFileReferences = updatedAnswer.MediaBankFileReferences;
                        answer.Comments = updatedAnswer.Comments;
                    }
                });
                args.afterChange();
            }
        }, args.ngOnDestroy$);
    }
}
